import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthActions } from "@convex-dev/auth/react";
import { useConvexAuth } from "convex/react";
import { Alert, Button, InputWithLabel } from "../../../components/atoms/atoms.jsx";
import { useUserStore } from "../Store/userStore";
import convex from "../../../convexClient";
import { api } from "../../../convex/_generated/api";
import { useTenantStore } from "../../../stores/useTenantStore";

const getPasswordStrength = (pw) => {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
};

const STRENGTH_LABELS = ["", "Weak", "Fair", "Good", "Strong"];
const STRENGTH_COLORS = [
  "",
  "bg-red-400",
  "bg-amber-400",
  "bg-yellow-400",
  "bg-emerald-500",
];
const STRENGTH_TEXT = [
  "",
  "text-red-500",
  "text-amber-500",
  "text-yellow-600",
  "text-emerald-600",
];

const SignUpForm = ({ title, buttonName }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuthActions();
  const { isAuthenticated } = useConvexAuth();
  const { showPassword, togglePasswordVisibility, validatePassword, mapAuthErrorToMessage } = useUserStore();
  const hasHandledAuthenticatedSession = useRef(false);

  const strength = getPasswordStrength(password);

  useEffect(() => {
    if (!isAuthenticated) {
      hasHandledAuthenticatedSession.current = false;
      return;
    }
    if (hasHandledAuthenticatedSession.current) return;
    hasHandledAuthenticatedSession.current = true;

    const setup = async () => {
      const next = new URLSearchParams(location.search).get("next");

      if (next) {
        navigate(next, { replace: true });
        return;
      }

      try {
        const tenants = await convex.query(api.tenants.getUserTenants, {});
        if (tenants.length > 0) {
          await useTenantStore.getState().fetchUserTenants();
          navigate(`/${tenants[0].slug}/home`, { replace: true });
          return;
        }
        navigate("/get-started", { replace: true });
      } catch (err) {
        console.error("[SignUpForm] post-auth redirect:", err);
        navigate("/get-started", { replace: true });
      }
    };

    setup();
  }, [isAuthenticated, location.search, navigate]);

  useEffect(() => {
    if (!localError) return;
    const timer = setTimeout(() => setLocalError(null), 5000);
    return () => clearTimeout(timer);
  }, [localError]);

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const onSignUp = async (e) => {
    e.preventDefault();
    if (!firstName.trim()) { setLocalError("Enter your first name."); return; }
    if (!email) { setLocalError("Enter your email address."); return; }
    const passwordError = validatePassword(password);
    if (passwordError) { setLocalError(passwordError); return; }
    if (password !== confirmPassword) { setLocalError("Passwords don't match. Check and try again."); return; }
    if (!agreeToTerms) { setLocalError("Agree to the terms and conditions to continue."); return; }

    setIsSubmitting(true);
    try {
      // Store name for onboarding pre-fill before auth completes
      try {
        sessionStorage.setItem(
          "onboarding_name",
          JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim() })
        );
      } catch {}

      const next = new URLSearchParams(location.search).get("next");
      await signIn("password", { flow: "signUp", email, password });
      setSuccessMessage(
        next
          ? "Account created! Redirecting to your invitation…"
          : "Account created! Setting up your workspace…"
      );
    } catch (err) {
      setLocalError(mapAuthErrorToMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="lg:container max-w-xl mx-auto">
      {localError && <Alert variant="destructive">{localError}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}

      <h1 className="text-2xl text-primary font-bold mb-5">{title || "Create your account"}</h1>

      <form onSubmit={onSignUp}>
        {/* Name row */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <InputWithLabel
            inputId="first-name"
            labelName="First name"
            inputType="text"
            inputPlaceholder="Akua"
            inputRequired
            onChange={(e) => setFirstName(e.target.value)}
            inputValue={firstName}
            autoFocus
          />
          <InputWithLabel
            inputId="last-name"
            labelName="Last name"
            inputType="text"
            inputPlaceholder="Mensah"
            onChange={(e) => setLastName(e.target.value)}
            inputValue={lastName}
          />
        </div>

        {/* Email */}
        <div className="mb-5">
          <InputWithLabel
            inputId="email"
            labelName="Email address"
            inputType="email"
            inputPlaceholder="you@example.com"
            inputRequired
            onChange={(e) => setEmail(e.target.value)}
            inputValue={email}
          />
        </div>

        {/* Password + strength */}
        <div className="mb-5">
          <InputWithLabel
            inputId="password"
            labelName="Password"
            inputType={showPassword ? "text" : "password"}
            inputPlaceholder="At least 8 characters"
            inputRequired
            onChange={(e) => setPassword(e.target.value)}
            inputValue={password}
          />
          {password.length > 0 && (
            <div className="mt-2 space-y-1">
              <div className="flex gap-1">
                {[1, 2, 3, 4].map((level) => (
                  <div
                    key={level}
                    className={`h-1 flex-1 rounded-full transition-colors ${
                      strength >= level ? STRENGTH_COLORS[strength] : "bg-gray-200"
                    }`}
                  />
                ))}
              </div>
              <p className={`text-xs font-medium ${STRENGTH_TEXT[strength]}`}>
                {STRENGTH_LABELS[strength]}
              </p>
            </div>
          )}
          <div
            className="font-medium py-2 text-sm cursor-pointer"
            onClick={togglePasswordVisibility}
          >
            {showPassword ? "Hide" : "Show"} password
          </div>
        </div>

        {/* Confirm password */}
        <div className="mb-5">
          <InputWithLabel
            inputId="confirm-password"
            labelName="Confirm password"
            inputType={showPassword ? "text" : "password"}
            inputPlaceholder="Re-enter your password"
            inputRequired
            onChange={(e) => setConfirmPassword(e.target.value)}
            inputValue={confirmPassword}
          />
          {confirmPassword.length > 0 && password !== confirmPassword && (
            <p className="mt-1 text-xs text-red-500">Passwords don't match</p>
          )}
        </div>

        {/* Terms */}
        <label className="inline-flex items-center mb-5">
          <input
            type="checkbox"
            className="h-4 w-4 accent-primary border-gray-300 rounded"
            onChange={(e) => setAgreeToTerms(e.target.checked)}
            checked={agreeToTerms}
          />
          <span className="ml-2 text-sm">
            I agree to the{" "}
            <a href="/terms" className="text-blue-500 underline" target="_blank" rel="noreferrer">
              Terms &amp; Conditions
            </a>
            {" "}and{" "}
            <a href="/privacy" className="text-blue-500 underline" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
            .
          </span>
        </label>

        <Button
          className="w-full"
          name={isSubmitting ? "Creating account…" : (buttonName || "Create account")}
          disabled={isSubmitting}
        />
      </form>

      <p className="mt-5 text-center text-sm text-gray-500">
        Already have an account?{" "}
        <button
          type="button"
          className="font-semibold text-primary hover:underline"
          onClick={() => navigate("/sign-in")}
        >
          Sign in
        </button>
      </p>
    </div>
  );
};

export default SignUpForm;
